import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../components/ui/table";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
// 29/09/2026 — date gg/mm/aaaa da un'unica fonte (qui c'era fmtDate).
import { formatDate } from "../lib/format";
import {
  History,
  CheckCircle2,
  XCircle,
  RotateCcw,
  Search,
  RefreshCw,
  ClipboardList,
} from "lucide-react";

const STATUS_META = {
  da_fare: { label: "Da fare", tone: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" },
  fatto: { label: "Fatto", tone: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" },
  non_rinnovato: { label: "Non rinnovato", tone: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" },
  particolare: { label: "Particolare", tone: "bg-muted text-muted-foreground" },
};

const MONTHS = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];

function fmtEuro(v) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.particolare;
  return <Badge className={`${m.tone} shrink-0`}>{m.label}</Badge>;
}

function NotesCell({ id, initialNotes }) {
  const [value, setValue] = useState(initialNotes || "");
  const [saving, setSaving] = useState(false);

  async function save() {
    const v = value.trim();
    if (v === (initialNotes || "").trim()) return;
    setSaving(true);
    try {
      await axios.post(`/api/legacy/scadenze/${id}/notes`, { notes: v });
    } catch (err) {
      toast.error("Errore salvataggio note: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Input
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={save}
      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
      placeholder="Prezzi, note…"
      className="h-8 text-xs min-w-[190px]"
      disabled={saving}
    />
  );
}

export default function RecuperoScadenzePage() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("da_fare");
  const [month, setMonth] = useState("all");
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState(null);

  async function reload() {
    setLoading(true);
    try {
      const params = { status, limit: 500 };
      if (month !== "all") params.month = month;
      if (search.trim()) params.search = search.trim();
      const { data } = await axios.get("/api/legacy/scadenze", { params });
      setItems(data.items || []);
    } catch (err) {
      toast.error("Errore caricamento scadenze: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }

  async function reloadStats() {
    try {
      const { data } = await axios.get("/api/legacy/stats");
      setStats(data);
    } catch {
      // silent
    }
  }

  useEffect(() => {
    reload();
    reloadStats();
  }, []);

  useEffect(() => {
    reload();
  }, [status, month]);

  async function setStatusById(id, newStatus) {
    setBusyId(id);
    try {
      await axios.post(`/api/legacy/scadenze/${id}/status`, { status: newStatus });
      toast.success(
        newStatus === "fatto" ? "Segnata come fatta ✅"
        : newStatus === "non_rinnovato" ? "Segnata come non rinnovata ⛔"
        : "Ripristinata come da fare."
      );
      await reload();
      await reloadStats();
    } catch (err) {
      toast.error("Errore: " + (err.response?.data?.detail || err.message));
    } finally {
      setBusyId(null);
    }
  }

  const counts = stats?.counts || {};
  const filtered = useMemo(() => items, [items]);

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground tracking-tight flex items-center gap-3">
            <History className="h-8 w-8 text-primary" />
            Recupero scadenze
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Archivio vecchio (portafoglio Excel 2026). Si svuota via via che rinnovi le polizze.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { reload(); reloadStats(); }}>
          <RefreshCw className="h-3.5 w-3.5 mr-2" />
          Aggiorna
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-amber-300/60 dark:border-amber-800/40">
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground">Da fare</div>
            <div className="text-2xl font-bold">{counts.da_fare ?? "—"}</div>
            <div className="text-[11px] text-muted-foreground">
              {stats ? `${stats.prossimi_30} nei prox 30gg · ${stats.prossimi_60} nei 60gg` : ""}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground">Già in InsureAssist</div>
            <div className="text-2xl font-bold text-green-600">{counts.fatto ?? "—"}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground">Non rinnovate</div>
            <div className="text-2xl font-bold text-red-600">{counts.non_rinnovato ?? "—"}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground">Casi particolari</div>
            <div className="text-2xl font-bold text-muted-foreground">{counts.particolare ?? "—"}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filtri */}
      <Card>
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-[170px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="da_fare">Da fare</SelectItem>
              <SelectItem value="fatto">Fatto</SelectItem>
              <SelectItem value="non_rinnovato">Non rinnovato</SelectItem>
              <SelectItem value="particolare">Particolare</SelectItem>
              <SelectItem value="tutte">Tutte</SelectItem>
            </SelectContent>
          </Select>

          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i mesi</SelectItem>
              {MONTHS.map((m, i) => (
                <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex items-center gap-2 flex-1 min-w-[200px]">
            <Search className="h-4 w-4 text-muted-foreground shrink-0" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && reload()}
              placeholder="Cerca nome o targa… (Invio)"
              className="flex-1"
            />
          </div>

          <Button variant="outline" size="sm" onClick={reload}>
            <ClipboardList className="h-3.5 w-3.5 mr-2" />
            {items.length} risultati
          </Button>
        </CardContent>
      </Card>

      {/* Tabella */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-5 space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full rounded-lg" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-12">
              Nessuna scadenza con questi filtri.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Scadenza</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead className="hidden md:table-cell">Targa</TableHead>
                    <TableHead className="hidden md:table-cell">Ramo</TableHead>
                    <TableHead className="hidden md:table-cell">Compagnia</TableHead>
                    <TableHead className="hidden md:table-cell text-right">Premio</TableHead>
                    <TableHead className="hidden md:table-cell text-right">Comp.</TableHead>
                    <TableHead className="text-right">Totale</TableHead>
                    <TableHead className="hidden md:table-cell">Produttore</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead className="hidden md:table-cell">Note</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell className="whitespace-nowrap">{formatDate(e.expiry_date, "—")}</TableCell>
                      <TableCell className="font-medium whitespace-nowrap">{e.client_name}</TableCell>
                      <TableCell className="hidden md:table-cell font-mono">{e.plate || "—"}</TableCell>
                      <TableCell className="hidden md:table-cell">{e.branch || "—"}</TableCell>
                      <TableCell className="hidden md:table-cell">{e.company || "—"}</TableCell>
                      <TableCell className="hidden md:table-cell text-right whitespace-nowrap">{fmtEuro(e.premium_net)}</TableCell>
                      <TableCell className="hidden md:table-cell text-right whitespace-nowrap">{fmtEuro(e.commission)}</TableCell>
                      <TableCell className="text-right whitespace-nowrap font-medium">
                        {fmtEuro((Number(e.premium_net) || 0) + (Number(e.commission) || 0))}
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-muted-foreground">{e.producer || "—"}</TableCell>
                      <TableCell><StatusBadge status={e.status} /></TableCell>
                      <TableCell className="hidden md:table-cell"><NotesCell id={e.id} initialNotes={e.notes} /></TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {e.status === "da_fare" && (
                            <>
                              <Button size="sm" disabled={busyId === e.id} onClick={() => setStatusById(e.id, "fatto")}>
                                <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Fatto
                              </Button>
                              <Button size="sm" variant="outline" disabled={busyId === e.id} onClick={() => setStatusById(e.id, "non_rinnovato")}>
                                <XCircle className="h-3.5 w-3.5 mr-1" /> Non rinnovato
                              </Button>
                            </>
                          )}
                          {(e.status === "fatto" || e.status === "non_rinnovato") && (
                            <Button size="sm" variant="ghost" disabled={busyId === e.id} onClick={() => setStatusById(e.id, "da_fare")}>
                              <RotateCcw className="h-3.5 w-3.5 mr-1" /> Ripristina
                            </Button>
                          )}
                          {e.status === "particolare" && <span className="text-xs text-muted-foreground">—</span>}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
